import { DeviceBasicSettings } from '@/components/device/device-basic-settings'
import { IntegrationConfig } from '@/components/device/integration-config'
import { RestaurantVariables } from '@/components/device/restaurant-variables'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { UserProfileForm } from '@/components/user/user-profile-form'
import { Globe, Settings, Sliders, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function SettingsDialog({ open, onOpenChange }: SettingsDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            {t('navigation.settings')}
          </DialogTitle>
          <DialogDescription>
            {t('settingsDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="base" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3 shrink-0">
            <TabsTrigger value="base">
              <Sliders className="h-4 w-4 mr-2" />
              {t('settingsDialog.tabs.base')}
            </TabsTrigger>
            <TabsTrigger value="integration">
              <Globe className="h-4 w-4 mr-2" />
              {t('settingsDialog.tabs.integration')}
            </TabsTrigger>
            <TabsTrigger value="profile">
              <User className="h-4 w-4 mr-2" />
              {t('settingsDialog.tabs.profile')}
            </TabsTrigger>
          </TabsList>

          {/* Base Settings Tab - Combined */}
          <TabsContent value="base" className="space-y-4 overflow-y-auto flex-1 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('settingsDialog.tabs.base')}</CardTitle>
                <CardDescription>
                  {t('settingsDialog.baseDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <DeviceBasicSettings />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('settingsDialog.restaurantData')}</CardTitle>
                <CardDescription>
                  {t('settingsDialog.restaurantDataDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RestaurantVariables />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integration Tab */}
          <TabsContent value="integration" className="space-y-4 overflow-y-auto flex-1 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('settingsDialog.integrationTitle')}</CardTitle>
                <CardDescription>
                  {t('settingsDialog.integrationDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <IntegrationConfig />
              </CardContent>
            </Card>
          </TabsContent>

          {/* User Profile Tab */}
          <TabsContent value="profile" className="space-y-4 overflow-y-auto flex-1 mt-4">
            <Card>
              <CardHeader>
                <CardTitle>{t('settingsDialog.tabs.profile')}</CardTitle>
                <CardDescription>
                  {t('settingsDialog.profileDescription')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <UserProfileForm />
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        <div className="flex justify-end pt-4 border-t shrink-0 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('global.button.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
